// src/screens/CreateTeam.tsx
import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Users, Crown, Save, ShieldAlert } from "lucide-react";
import i18n from "../i18n";

// mini hook i18n safe
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

type FormInputs = { teamName: string; league: string; };

export const CreateTeam: React.FC = () => {
  const { t } = useI18nSafe();
  const { register, handleSubmit, reset } = useForm<FormInputs>();
  const [isPremium, setIsPremium] = useState(false);
  const [teamCount, setTeamCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const limit = isPremium ? 3 : 1;
  const usagePct = Math.min(100, Math.round((teamCount / limit) * 100));

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data: profile } = await supabase
        .from("user_profiles")
        .select("is_premium")
        .eq("id", user.id)
        .single();
      setIsPremium(!!profile?.is_premium);

      const { data: teams } = await supabase
        .from("teams")
        .select("id")
        .eq("user_id", user.id)
        .or("archived.is.null,archived.eq.false");

      setTeamCount(teams?.length ?? 0);
      setLoading(false);
    };
    init();
  }, []);

  const onSubmit: SubmitHandler<FormInputs> = async ({ teamName, league }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    if (teamCount >= limit) {
      alert(
        isPremium
          ? t("createteam:limit_reached_premium", { limit })
          : t("createteam:limit_reached_free", { limit })
      );
      return;
    }

    const name = teamName.trim();
    if (!name) { alert(t("createteam:team_name_required")); return; }

    const { data, error } = await supabase
      .from("teams")
      .insert([{ user_id: user.id, team_name: name, league: (league || "").trim(), players: [] }])
      .select("id")
      .single();

    if (error || !data) {
      console.error(error);
      alert(t("createteam:create_error"));
      return;
    }

    reset();
    navigate(`/teams/${data.id}/players`);
  };

  if (loading) {
    return (
      <section className="min-h-screen p-6 bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
        <p className="text-center text-gray-600 dark:text-gray-300">{t("common:loading")}</p>
      </section>
    );
  }

  return (
    <section className="min-h-screen w-full p-6 mx-auto bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <div className="max-w-xl mx-auto">
        <h1 className="text-2xl font-bold mb-6 text-center text-orange-600 dark:text-orange-400">
          {t("createteam:title")}
        </h1>

        <div className="rounded-2xl border bg-white border-gray-200 shadow-sm p-6 dark:bg-gray-900 dark:border-gray-800">
          {/* Bandeau état / quota */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="inline-flex items-center gap-2 text-sm">
              <Users className="w-5 h-5 text-gray-500" />
              <span className="text-gray-700 dark:text-gray-300">
                {isPremium
                  ? t("createteam:usage_premium", { used: teamCount, limit })
                  : t("createteam:usage_free", { used: teamCount, limit })}
              </span>
            </div>

            {isPremium ? (
              <span className="inline-flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <Crown className="w-4 h-4" />
                {t("createteam:premium_active")}
              </span>
            ) : (
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <ShieldAlert className="w-4 h-4" />
                <span>{t("createteam:free_limit_note", { limit: 1 })}</span>
                <Link
                  to="/upgrade"
                  className="ml-2 underline text-orange-600 dark:text-orange-400 hover:opacity-80"
                >
                  {t("createteam:upgrade")}
                </Link>
              </div>
            )}
          </div>

          {/* Barre de progression quota */}
          <div className="h-2 w-full rounded bg-gray-200 dark:bg-gray-800 mb-6 overflow-hidden">
            <div
              className={`h-full ${usagePct >= 100 ? "bg-red-500" : "bg-blue-600"}`}
              style={{ width: `${usagePct}%` }}
            />
          </div>

          {/* Formulaire */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
                {t("createteam:labels.team_name")}
              </label>
              <input
                {...register("teamName", { required: true })}
                placeholder={t("createteam:placeholders.team_name")}
                className="w-full rounded-lg border px-3 py-2
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
                {t("createteam:labels.league")}
              </label>
              <input
                {...register("league", { required: true })}
                placeholder={t("createteam:placeholders.league")}
                className="w-full rounded-lg border px-3 py-2
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
              />
            </div>

            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 font-semibold py-2 px-6 rounded
                         bg-[#fc7825] hover:bg-orange-600 text-white
                         focus:outline-none focus:ring-2 focus:ring-orange-400/60"
            >
              <Save className="w-4 h-4" />
              {t("createteam:cta")}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default CreateTeam;
